import React from 'react';
import { Smartphone, TrendingUp, Target, Network, Mail, Globe } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Target,
      title: 'Programmatic Media Buying',
      description: 'Advanced programmatic direct media buying designed to provide superior transparency, insights, and scalable performance for your campaigns.',
      features: ['Real-time bidding', 'Audience targeting', 'Performance tracking'],
    },
    {
      icon: Network,
      title: 'Multi-Channel Solutions',
      description: 'Create a seamless and consistent customer experience across all channels and touchpoints with our integrated approach.',
      features: ['Cross-channel strategy', 'Unified brand experience', 'Seamless integration'],
    },
    {
      icon: Smartphone,
      title: 'Mobile App Marketing',
      description: 'Industry-leading performance marketing solutions specifically designed for mobile app advertisers with proven results.',
      features: ['App install campaigns', 'User acquisition', 'In-app engagement'],
    },
    {
      icon: TrendingUp,
      title: 'Lead Generation',
      description: 'Targeted lead generation strategies that deliver high-quality prospects through data-driven optimization and multi-channel approach.',
      features: ['Quality lead scoring', 'Conversion optimization', 'Multi-touch attribution'],
    },
    {
      icon: Globe,
      title: 'Display & Native Advertising',
      description: 'Comprehensive display and native advertising solutions across premium publishers to maximize reach and engagement.',
      features: ['Programmatic display', 'Native placements', 'Creative optimization'],
    },
    {
      icon: Mail,
      title: 'Email & Social Marketing',
      description: 'Strategic email and social media marketing campaigns that drive engagement and conversions across all major platforms.',
      features: ['Email automation', 'Social campaigns', 'Audience segmentation'],
    },
  ];

  return (
    <section id="services" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 backdrop-blur-sm mb-6">
            <span className="text-sm text-cyan-400 font-medium">Our Services</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Comprehensive Performance
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Marketing Solutions
            </span>
          </h2>
          <p className="text-xl text-slate-400 leading-relaxed">
            All channels are interconnected and work together to create a cohesive solution that drives measurable results.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 group-hover:bg-cyan-500/20 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">{service.description}</p>

                  {/* Features */}
                  <ul className="space-y-2 pt-4 border-t border-slate-800/50">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-slate-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover Effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
